import React, { useCallback, useEffect, useState } from "react";
import PageHeader from "../components/shared/pageHeader/PageHeader";
import {
  FiArrowLeft,
  FiEdit,
  FiEye,
  FiEyeOff,
  FiFolder,
  FiGrid,
  FiInbox,
  FiLink,
  FiLoader,
  FiPlus,
  FiRefreshCw,
  FiSave,
  FiTrash2,
} from "react-icons/fi";
import { RxDragHandleDots2 } from "react-icons/rx";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import "react-toastify/dist/ReactToastify.css";
import "./CustomSwitchPage.css";
import "./CategoryDataManagerPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const emptyCategoryForm = {
  title: "",
  description: "",
};

const emptyItemForm = {
  title: "",
  description: "",
  url: "",
};

const createTempId = () => `tmp-${Date.now()}-${Math.random()}`;

// Her kategoriye, sırasına göre döngüsel olarak atanan bir vurgu rengi.
// Bu sayede kategoriler tek bakışta görsel olarak ayrılır.
const ACCENT_PALETTE = [
  { accent: "#6366f1", soft: "rgba(99, 102, 241, 0.12)" },
  { accent: "#14b8a6", soft: "rgba(20, 184, 166, 0.12)" },
  { accent: "#f59e0b", soft: "rgba(245, 158, 11, 0.14)" },
  { accent: "#f43f5e", soft: "rgba(244, 63, 94, 0.12)" },
  { accent: "#8b5cf6", soft: "rgba(139, 92, 246, 0.12)" },
  { accent: "#0ea5e9", soft: "rgba(14, 165, 233, 0.12)" },
];

const getAccent = (index) => ACCENT_PALETTE[index % ACCENT_PALETTE.length];

const CategoryDataManagerPage = () => {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [items, setItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [itemLoading, setItemLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [categoryModalMode, setCategoryModalMode] = useState("add");
  const [categoryEditIndex, setCategoryEditIndex] = useState(null);
  const [categoryForm, setCategoryForm] = useState(emptyCategoryForm);

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemModalMode, setItemModalMode] = useState("add");
  const [itemEditIndex, setItemEditIndex] = useState(null);
  const [itemForm, setItemForm] = useState(emptyItemForm);

  const fetchCategories = useCallback(async () => {
    setLoading(true);

    try {
      const resp = await axios.get(`${API_BASE}/api/category-data/categories`);
      const data = Array.isArray(resp.data) ? resp.data : [];

      setCategories(
        data
          .map((cat, index) => ({
            ...cat,
            title: cat.title || "",
            description: cat.description || "",
            sort_order: Number(cat.sort_order ?? index),
          }))
          .sort((a, b) => Number(a.sort_order) - Number(b.sort_order)),
      );
    } catch (error) {
      console.error(error);
      toast.error("Kategoriler yüklenemedi.");
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const fetchItems = async (category) => {
    setSelectedCategory(category);
    setItemLoading(true);

    try {
      const resp = await axios.get(
        `${API_BASE}/api/category-data/categories/${category.id}/items`,
      );

      const data = Array.isArray(resp.data) ? resp.data : [];

      setItems(
        data
          .map((item, index) => ({
            ...item,
            temp_id: item.temp_id || createTempId(),
            title: item.title || "",
            description: item.description || "",
            url: item.url || "",
            is_visible:
              item.is_visible === true || Number(item.is_visible) === 1,
            sort_order: Number(item.sort_order ?? index),
          }))
          .sort((a, b) => Number(a.sort_order) - Number(b.sort_order)),
      );
    } catch (error) {
      console.error(error);
      toast.error("Veriler yüklenemedi.");
    }

    setItemLoading(false);
  };

  const backToCategories = () => {
    setSelectedCategory(null);
    setItems([]);
  };

  const openAddCategory = () => {
    setCategoryModalMode("add");
    setCategoryEditIndex(null);
    setCategoryForm(emptyCategoryForm);
    setCategoryModalOpen(true);
  };

  const openEditCategory = (index) => {
    const cat = categories[index];

    setCategoryModalMode("edit");
    setCategoryEditIndex(index);
    setCategoryForm({
      title: cat.title || "",
      description: cat.description || "",
    });
    setCategoryModalOpen(true);
  };

  const closeCategoryModal = () => {
    setCategoryModalOpen(false);
    setCategoryModalMode("add");
    setCategoryEditIndex(null);
    setCategoryForm(emptyCategoryForm);
  };

  const saveCategory = async () => {
    if (!categoryForm.title.trim()) {
      toast.warning("Kategori başlığı boş olamaz.");
      return;
    }

    try {
      const payload = {
        title: categoryForm.title.trim(),
        description: categoryForm.description.trim(),
      };

      if (categoryModalMode === "add") {
        await axios.post(`${API_BASE}/api/category-data/categories`, payload);
        toast.success("Kategori eklendi.");
      } else {
        const category = categories[categoryEditIndex];

        await axios.put(
          `${API_BASE}/api/category-data/categories/${category.id}`,
          payload,
        );

        toast.success("Kategori güncellendi.");
      }

      closeCategoryModal();
      await fetchCategories();
    } catch (error) {
      console.error(error);
      toast.error("Kategori kaydedilemedi.");
    }
  };

  const deleteCategory = async (index) => {
    const category = categories[index];

    if (!window.confirm("Bu kategori ve içindeki veriler silinsin mi?")) return;

    try {
      await axios.delete(
        `${API_BASE}/api/category-data/categories/${category.id}`,
      );
      toast.success("Kategori silindi.");

      await fetchCategories();

      if (selectedCategory?.id === category.id) {
        backToCategories();
      }
    } catch (error) {
      console.error(error);
      toast.error("Kategori silinemedi.");
    }
  };

  const openAddItem = () => {
    setItemModalMode("add");
    setItemEditIndex(null);
    setItemForm(emptyItemForm);
    setItemModalOpen(true);
  };

  const openEditItem = (index) => {
    const item = items[index];

    setItemModalMode("edit");
    setItemEditIndex(index);
    setItemForm({
      title: item.title || "",
      description: item.description || "",
      url: item.url || "",
    });
    setItemModalOpen(true);
  };

  const closeItemModal = () => {
    setItemModalOpen(false);
    setItemModalMode("add");
    setItemEditIndex(null);
    setItemForm(emptyItemForm);
  };

  const saveItemsToApi = async (updatedItems) => {
    if (!selectedCategory) return;

    await axios.post(
      `${API_BASE}/api/category-data/categories/${selectedCategory.id}/items/save`,
      {
        items: updatedItems.map((item, index) => ({
          id: item.id,
          title: item.title,
          description: item.description,
          url: item.url,
          is_visible: !!item.is_visible,
          sort_order: index,
        })),
      },
    );
  };

  const saveItemModal = async () => {
    if (!itemForm.title.trim()) {
      toast.warning("Başlık boş olamaz.");
      return;
    }

    const updated = [...items];

    if (itemModalMode === "add") {
      updated.push({
        id: null,
        temp_id: createTempId(),
        category_id: selectedCategory.id,
        title: itemForm.title.trim(),
        description: itemForm.description.trim(),
        url: itemForm.url.trim(),
        is_visible: true,
        sort_order: updated.length,
      });
    } else {
      updated[itemEditIndex] = {
        ...updated[itemEditIndex],
        title: itemForm.title.trim(),
        description: itemForm.description.trim(),
        url: itemForm.url.trim(),
      };
    }

    const orderedItems = updated.map((item, index) => ({
      ...item,
      sort_order: index,
    }));

    try {
      await saveItemsToApi(orderedItems);

      toast.success(
        itemModalMode === "add" ? "Veri eklendi." : "Veri güncellendi.",
      );

      closeItemModal();
      await fetchItems(selectedCategory);
    } catch (error) {
      console.error(error);
      toast.error("Veri kaydedilemedi.");
    }
  };

  const deleteItem = async (index) => {
    const item = items[index];

    if (!window.confirm("Bu veri silinsin mi?")) return;

    try {
      if (item.id) {
        await axios.delete(`${API_BASE}/api/category-data/items/${item.id}`);
      }

      setItems((prev) =>
        prev
          .filter((_, i) => i !== index)
          .map((item, i) => ({
            ...item,
            sort_order: i,
          })),
      );

      toast.success("Veri silindi.");
    } catch (error) {
      console.error(error);
      toast.error("Veri silinemedi.");
    }
  };

  const toggleItemVisible = async (index) => {
    const oldItems = [...items];

    const updated = items.map((item, itemIndex) =>
      itemIndex === index
        ? {
            ...item,
            is_visible: !item.is_visible,
          }
        : item,
    );

    setItems(updated);

    try {
      await saveItemsToApi(updated);

      toast.success(
        updated[index].is_visible
          ? "Veri aktif yapıldı."
          : "Veri pasif yapıldı.",
      );
    } catch (error) {
      console.error(error);
      setItems(oldItems);
      toast.error("Durum güncellenemedi.");
    }
  };

  const reorder = (list, sourceIndex, destIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(sourceIndex, 1);
    result.splice(destIndex, 0, removed);

    return result.map((item, index) => ({
      ...item,
      sort_order: index,
    }));
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const oldItems = [...items];

    const updated = reorder(
      items,
      result.source.index,
      result.destination.index,
    );

    setItems(updated);

    try {
      await saveItemsToApi(updated);
      toast.success("Sıralama güncellendi.");
    } catch (error) {
      console.error(error);
      setItems(oldItems);
      toast.error("Sıralama kaydedilemedi.");
    }
  };

  return (
    <>
      <PageHeader />

      <div className="main-content">
        <div className="row">
          <div className="col-12 page-content p-4">
            <ToastContainer />

            {!selectedCategory && (
              <>
                <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div className="cdm-page-head">
                    <div>
                      <h5 className="mb-1">
                        Kategori ve Veri Yönetimi
                        {!loading && categories.length > 0 && (
                          <span className="cdm-count-pill">
                            {categories.length} kategori
                          </span>
                        )}
                      </h5>
                      <p className="text-muted mb-0">
                        Önce kategori oluştur, sonra içine kayıt ekle.
                      </p>
                    </div>
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-secondary btn-sm cdm-btn d-flex align-items-center"
                      onClick={fetchCategories}
                      disabled={loading}
                    >
                      <FiRefreshCw
                        className={`me-1 ${loading ? "cdm-spin" : ""}`}
                      />
                      Yenile
                    </button>

                    <button
                      className="btn btn-sm cdm-btn cdm-btn-primary text-white d-flex align-items-center"
                      onClick={openAddCategory}
                    >
                      <FiPlus className="me-1" />
                      Yeni Ekle
                    </button>
                  </div>
                </div>

                <div className="card border-0">
                  <div className="card-body">
                    {loading && (
                      <div className="cdm-state">
                        <div className="cdm-state-icon">
                          <FiLoader className="cdm-spin" />
                        </div>
                        <h6>Kategoriler yükleniyor</h6>
                        <p>Bir saniye sürer, hemen geliyor.</p>
                      </div>
                    )}

                    {!loading && categories.length === 0 && (
                      <div className="cdm-state">
                        <div className="cdm-state-icon">
                          <FiInbox />
                        </div>
                        <h6>Henüz kategori yok</h6>
                        <p>
                          Başlamak için ilk kategorini oluştur, ardından içine
                          istediğin kadar veri ekleyebilirsin.
                        </p>
                        <button
                          className="btn btn-sm cdm-btn cdm-btn-primary text-white d-flex align-items-center"
                          onClick={openAddCategory}
                        >
                          <FiPlus className="me-1" />
                          İlk Kategorini Oluştur
                        </button>
                      </div>
                    )}

                    {!loading &&
                      categories.map((category, index) => {
                        const { accent, soft } = getAccent(index);

                        return (
                          <div
                            key={category.id}
                            className="cdm-category-card"
                            style={{
                              "--cdm-accent": accent,
                              "--cdm-accent-soft": soft,
                            }}
                          >
                            <div
                              className="cdm-category-main"
                              onClick={() => fetchItems(category)}
                            >
                              <div className="cdm-category-icon">
                                <FiFolder />
                              </div>

                              <div className="cdm-category-text">
                                <strong>{category.title}</strong>
                              </div>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                              <button
                                className="btn btn-sm btn-outline-primary cdm-btn"
                                onClick={() => fetchItems(category)}
                              >
                                İçeriği Aç
                              </button>

                              <button
                                className="cdm-icon-btn avatar-text text-success bg-soft-success border-soft-success"
                                onClick={() => openEditCategory(index)}
                                title="Kategori düzenle"
                              >
                                <FiEdit />
                              </button>

                              <button
                                className="cdm-icon-btn avatar-text text-danger bg-soft-danger border-soft-danger"
                                onClick={() => deleteCategory(index)}
                                title="Kategori sil"
                              >
                                <FiTrash2 />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </>
            )}

            {selectedCategory && (
              <>
                <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
                  <div>
                    <button
                      className="btn btn-light btn-sm cdm-btn mb-2 d-flex align-items-center"
                      onClick={backToCategories}
                    >
                      <FiArrowLeft className="me-1" />
                      Kategorilere Dön
                    </button>

                    <h5 className="mb-1">
                      {selectedCategory.title}
                      {!itemLoading && items.length > 0 && (
                        <span className="cdm-count-pill">
                          {items.length} kayıt
                        </span>
                      )}
                    </h5>
                    <p className="text-muted mb-0">
                      Bu kategorinin içindeki verileri ekle, düzenle ve sırala.
                    </p>
                  </div>

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-outline-primary btn-sm cdm-btn d-flex align-items-center"
                      onClick={openAddItem}
                    >
                      <FiPlus className="me-1" />
                      Yeni Ekle
                    </button>
                  </div>
                </div>

                <div className="card border-0">
                  <div className="card-body">
                    {itemLoading && (
                      <div className="cdm-state">
                        <div className="cdm-state-icon">
                          <FiLoader className="cdm-spin" />
                        </div>
                        <h6>Veriler yükleniyor</h6>
                        <p>Bir saniye sürer, hemen geliyor.</p>
                      </div>
                    )}

                    {!itemLoading && items.length === 0 && (
                      <div className="cdm-state">
                        <div className="cdm-state-icon">
                          <FiInbox />
                        </div>
                        <h6>Bu kategoride veri yok</h6>

                        <button
                          className="btn btn-sm cdm-btn cdm-btn-primary text-white d-flex align-items-center"
                          onClick={openAddItem}
                        >
                          <FiPlus className="me-1" />
                          Yeni Ekle
                        </button>
                      </div>
                    )}

                    {!itemLoading && items.length > 0 && (
                      <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId="category-items">
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                            >
                              {items.map((item, index) => (
                                <Draggable
                                  key={String(item.id || item.temp_id)}
                                  draggableId={String(item.id || item.temp_id)}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      className={`cdm-item-row ${
                                        snapshot.isDragging ? "is-dragging" : ""
                                      }`}
                                      style={provided.draggableProps.style}
                                    >
                                      <div
                                        className="d-flex align-items-center gap-3"
                                        style={{ minWidth: 0 }}
                                      >
                                        <span
                                          {...provided.dragHandleProps}
                                          className="cdm-drag-handle"
                                        >
                                          <RxDragHandleDots2 size={18} />
                                        </span>

                                        <div style={{ minWidth: 0 }}>
                                          <strong>{item.title}</strong>

                                          {item.description && (
                                            <div className="text-muted small">
                                              {item.description}
                                            </div>
                                          )}

                                          {item.url && (
                                            <div className="cdm-item-link">
                                              <FiLink size={11} />
                                              {item.url}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      <div className="d-flex align-items-center gap-2 ms-auto justify-content-end">
                                        <div className="cdm-visibility">
                                          <label className="custom-switch-lg mb-0">
                                            <input
                                              type="checkbox"
                                              checked={!!item.is_visible}
                                              onChange={() =>
                                                toggleItemVisible(index)
                                              }
                                            />
                                            <span className="custom-slider"></span>
                                          </label>
                                        </div>

                                        <button
                                          className="cdm-icon-btn avatar-text text-success bg-soft-success border-soft-success"
                                          onClick={() => openEditItem(index)}
                                          title="Veri düzenle"
                                        >
                                          <FiEdit />
                                        </button>

                                        <button
                                          className="cdm-icon-btn avatar-text text-danger bg-soft-danger border-soft-danger"
                                          onClick={() => deleteItem(index)}
                                          title="Veri sil"
                                        >
                                          <FiTrash2 />
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              ))}

                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </DragDropContext>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {categoryModalOpen && (
        <div
          className="modal fade show cdm-modal-backdrop"
          style={{ display: "block", backgroundColor: "rgba(17,24,39,0.45)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content cdm-modal-content">
              <div className="modal-header">
                <div className="d-flex align-items-center">
                  <div className="cdm-modal-icon">
                    <FiFolder />
                  </div>
                  <h5 className="modal-title mb-0">
                    {categoryModalMode === "add"
                      ? "Yeni Ekle"
                      : "Kategori Düzenle"}
                  </h5>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={closeCategoryModal}
                ></button>
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Kategori Başlığı</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Örn. Sıkça Sorulan Sorular"
                    value={categoryForm.title}
                    onChange={(e) =>
                      setCategoryForm((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-light cdm-btn"
                  onClick={closeCategoryModal}
                >
                  İptal
                </button>

                <button
                  className="btn cdm-btn cdm-btn-primary text-white"
                  onClick={saveCategory}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {itemModalOpen && (
        <div
          className="modal fade show cdm-modal-backdrop"
          style={{ display: "block", backgroundColor: "rgba(17,24,39,0.45)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content cdm-modal-content">
              <div className="modal-header">
                <div className="d-flex align-items-center">
                  <div className="cdm-modal-icon">
                    <FiPlus />
                  </div>
                  <h5 className="modal-title mb-0">
                    {itemModalMode === "add" ? "Yeni Ekle" : "Veri Düzenle"}
                  </h5>
                </div>

                <button
                  type="button"
                  className="btn-close"
                  onClick={closeItemModal}
                ></button>
              </div>

              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Başlık</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Başlık"
                    value={itemForm.title}
                    onChange={(e) =>
                      setItemForm((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Açıklama</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Açıklama metni (opsiyonel)"
                    value={itemForm.description}
                    onChange={(e) =>
                      setItemForm((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-light cdm-btn"
                  onClick={closeItemModal}
                >
                  İptal
                </button>

                <button
                  className="btn cdm-btn cdm-btn-primary text-white"
                  onClick={saveItemModal}
                >
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoryDataManagerPage;
